import React, { useState, useEffect } from 'react';
import PageHeader from '@/components/PageHeader';
import Avatar from '@/components/Avatar';
import AddWorkspaceModal from '@/components/modals/AddWorkspaceModal';
import { useWorkspace } from '@/hooks/useData';
import { useToast } from '@/components/Toast';
import { I } from '@/lib/icons';
import { isValidIban, isValidIco } from '@/lib/validators';

export default function Nastavenia() {
  const { workspace, list, switchWorkspace, update } = useWorkspace();
  const toast = useToast();
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [addOpen, setAddOpen] = useState(false);

  useEffect(() => { if (workspace) setForm({ ...workspace }); }, [workspace]);

  if (!form) return null;

  const set = (k) => (v) => setForm(f => ({ ...f, [k]: typeof v === 'object' ? v.target.value : v }));
  const dirty = workspace && JSON.stringify(form) !== JSON.stringify(workspace);

  const save = async () => {
    // HI-04: validuj IBAN a IČO pred uložením, aby sa do faktúr nedostal
    // neplatný bankový účet alebo IČO. AddWorkspaceModal validuje pri vytvorení,
    // tu sa rovnaké pravidlo aplikuje pri editácii.
    const ibanClean = (form.iban || '').replace(/\s+/g, '');
    if (!isValidIban(ibanClean)) {
      toast.error('Zadaj platný slovenský IBAN (SK + 22 cifier).');
      return;
    }
    const icoClean = (form.ico || '').trim();
    if (!isValidIco(icoClean)) {
      toast.error('IČO musí mať 6 až 8 cifier.');
      return;
    }
    setSaving(true);
    try {
      const payload = {
        name: form.name, type: form.type, ico: icoClean, dic: form.dic,
        ic_dph: form.ic_dph || null, iban: ibanClean, bank: form.bank || null,
        bic: form.bic || null, address: form.address || null,
        email: form.email || null, phone: form.phone || null,
        is_dph_payer: !!form.is_dph_payer,
        default_dph_rate: Number(form.default_dph_rate) || 0,
        fa_number_format: form.fa_number_format || '{YYYY}{NNN}',
        notice: form.notice || null,
        brand_color: form.brand_color || '#2bbf85',
      };
      await update(payload);
      toast.success('Uložené.');
    } catch (e) { toast.error(e.message); }
    finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader title="Nastavenia" meta="Profil firmy" accent="gray"
        right={dirty && <button onClick={save} disabled={saving}
          data-testid="settings-save" className="cd-btn cd-btn-primary">
          {saving ? <><span className="cd-spinner" style={{ borderTopColor: '#fff' }} /> Ukladám...</>
            : <><I.check /> Uložiť zmeny</>}
        </button>}
      />

      <div style={{ padding: '12px 32px 48px', display: 'grid', gap: 14, maxWidth: 760 }}>
        {/* Moje firmy */}
        <Card title="Moje firmy">
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {list.map(ws => {
              const isCurrent = ws.id === workspace?.id;
              return (
                <div key={ws.id} style={{
                  display: 'flex', alignItems: 'center', gap: 12,
                  padding: '10px 12px',
                  background: isCurrent ? 'var(--bg-selected)' : 'var(--bg-alt)',
                  border: `1px solid ${isCurrent ? 'var(--product-primary)' : 'var(--border)'}`,
                  borderRadius: 'var(--r-md)',
                }}>
                  <Avatar name={ws.name} size={32} shape="square" />
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>{ws.name}</div>
                    <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>
                      {ws.type === 'sro' ? 's.r.o.' : 'SZČO'}
                    </div>
                  </div>
                  {isCurrent
                    ? <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--product-primary-hover)' }}>Aktívna</span>
                    : <button onClick={() => switchWorkspace(ws.id)}
                        className="cd-btn cd-btn-sm" style={{ background: 'transparent' }}>
                        Prepnúť
                      </button>}
                </div>
              );
            })}
          </div>
          <button
            type="button"
            data-testid="settings-add-workspace"
            onClick={() => setAddOpen(true)}
            className="cd-btn cd-btn-sm"
            style={{
              marginTop: 12, display: 'inline-flex', alignItems: 'center', gap: 6,
              background: 'transparent', border: '1px dashed var(--border)',
            }}
          >
            <I.plus width={12} height={12} /> Pridať novú firmu
          </button>
        </Card>

        {/* Profile card */}
        <Card title="Profil firmy">
          <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 16 }}>
            <Avatar name={form.name} size={64} shape="square" />
            <div>
              <div style={{ fontSize: 15, fontWeight: 600 }}>{form.name}</div>
              <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>
                {form.type === 'sro' ? 's.r.o.' : 'SZČO'} · avatar generovaný z initials
              </div>
            </div>
          </div>
          <FormGrid>
            <Field label="Typ firmy" full>
              <select className="cd-input" value={form.type} onChange={set('type')}
                style={{ padding: '9px 11px' }}>
                <option value="szco">SZČO (živnostník)</option>
                <option value="sro">s.r.o.</option>
              </select>
            </Field>
            <Field label="Názov" full><Input value={form.name} onChange={set('name')} testId="set-name" /></Field>
            <Field label="IČO"><Input mono value={form.ico} onChange={set('ico')} testId="set-ico" /></Field>
            <Field label="DIČ"><Input mono value={form.dic} onChange={set('dic')} /></Field>
            <Field label="Adresa" full><Input value={form.address || ''} onChange={set('address')} /></Field>
            <Field label="Email"><Input value={form.email || ''} onChange={set('email')} /></Field>
            <Field label="Telefón"><Input value={form.phone || ''} onChange={set('phone')} /></Field>
          </FormGrid>
        </Card>

        <Card title="DPH">
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', marginBottom: 14 }}>
            <input type="checkbox" checked={!!form.is_dph_payer}
              onChange={e => setForm(f => ({ ...f, is_dph_payer: e.target.checked }))}
              style={{ width: 18, height: 18 }} />
            <span style={{ fontSize: 13 }}>Som platca DPH</span>
          </label>
          {form.is_dph_payer && (
            <FormGrid>
              <Field label="IČ DPH" full><Input mono value={form.ic_dph || ''} onChange={set('ic_dph')} placeholder="SK1080123456" /></Field>
              <Field label="Predvolená sadzba DPH (%)">
                <select className="cd-input" value={form.default_dph_rate || 0}
                  onChange={e => setForm(f => ({ ...f, default_dph_rate: Number(e.target.value) }))}
                  style={{ padding: '9px 11px' }}>
                  <option value={0}>0%</option>
                  <option value={10}>10%</option>
                  <option value={20}>20%</option>
                  <option value={23}>23%</option>
                </select>
              </Field>
            </FormGrid>
          )}
        </Card>

        <Card title="Bankový účet">
          <FormGrid>
            <Field label="IBAN" full><Input mono value={form.iban} onChange={set('iban')} testId="set-iban" /></Field>
            <Field label="Banka" full><Input value={form.bank || ''} onChange={set('bank')} placeholder="Tatra banka, a.s." /></Field>
            <Field label="BIC / SWIFT"><Input mono value={form.bic || ''} onChange={set('bic')} placeholder="TATRSKBX" /></Field>
          </FormGrid>
        </Card>

        <Card title="Číslovanie faktúr">
          <Field label="Formát čísla FA" full>
            <Input mono value={form.fa_number_format || '{YYYY}{NNN}'} onChange={set('fa_number_format')} />
          </Field>
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 8, lineHeight: 1.5 }}>
            Použi <code>{'{YYYY}'}</code> pre rok, <code>{'{NNN}'}</code> alebo <code>{'{NNNN}'}</code> pre počítadlo. Najbližšie číslo: <strong>{form.fa_counter + 1}</strong>.
          </div>
        </Card>

        <Card title="Poznámka na faktúre">
          <textarea className="cd-input" value={form.notice || ''}
            onChange={set('notice')} rows={3}
            placeholder={form.is_dph_payer ? 'Všetky sumy sú s DPH 20%.' : 'Nie som platca DPH — oslobodenie podľa §4 ods. 1 zák. č. 222/2004 Z. z.'}
            style={{ resize: 'vertical' }} />
        </Card>

        <div style={{ marginTop: 8, fontSize: 12, color: 'var(--text-muted)', textAlign: 'center' }}>
          Verzia 0.1 · podnikai.sk
        </div>
      </div>
      <AddWorkspaceModal open={addOpen} onClose={() => setAddOpen(false)} />
    </>
  );
}

function Card({ title, children }) {
  return (
    <div style={{
      background: 'var(--bg-surface)', border: '1px solid var(--border)',
      borderRadius: 'var(--r-lg)', padding: 18,
    }}>
      <div style={{ fontSize: 13, fontWeight: 700, marginBottom: 14 }}>{title}</div>
      {children}
    </div>
  );
}
function FormGrid({ children }) {
  return <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>{children}</div>;
}
function Field({ label, full, children }) {
  return (
    <div style={{ gridColumn: full ? '1 / -1' : 'auto' }}>
      <div style={{
        fontSize: 10, fontWeight: 700, color: 'var(--text-muted)',
        textTransform: 'uppercase', letterSpacing: '0.07em', marginBottom: 5,
      }}>{label}</div>
      {children}
    </div>
  );
}
function Input({ value, onChange, mono, placeholder, testId }) {
  return (
    <input className={`cd-input${mono ? ' mono' : ''}`} data-testid={testId}
      value={value || ''} onChange={onChange} placeholder={placeholder} />
  );
}
